import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import axios from 'axios';

import { EVENT_INQUIRY_API_URL } from '@/constants/apiUrls';
import { LOCAL_LANGUAGE } from '@/constants/storageKey';
import {
  useGetSegmentCategory,
  useGetMeetingEventCategory,
  usePostEmailForm,
} from '@/hooks/CommonRequests';
import { EventClientInformation, EventRequestDetails } from '@/pages/event/index';
import { InputForm } from '@/pages/inquiry/inquryStyles';
import { zodEvent } from '@/utils/zodValidation';
import {
  changeDateFormat,
  eventEmailIdPerLanguage,
  getCodeName,
  removeCommaFromNumber,
} from '@/utils/functions';

const EventForm = ({ step, setStep, eventType }) => {
  const intl = useIntl();

  const navigate = useNavigate();
  const lang = localStorage.getItem(LOCAL_LANGUAGE);

  const { data: meetingEventCategory } = useGetMeetingEventCategory();
  const { data: segmentCategory } = useGetSegmentCategory();

  const [submittedData, setSubmittedData] = useState({});

  const {
    control,
    handleSubmit,
    watch,
    trigger,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(zodEvent),
    mode: 'all',
    defaultValues: {
      event_type: eventType,
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      company_name: '',
      segment: '',
      event_name: '',
      attendees: '',
      rooms: '',
      arrival_date: '',
      departure_date: '',
      comments: '',
      termsAll: false,
      termsReq1: false,
      termsReq2: false,
      captcha: false,
    },
  });

  const {
    mutate: postEmailForm,
    isSuccess: sendEmailSuccess,
    isPending: isEmailPending,
  } = usePostEmailForm();

  const {
    mutate: eventMutate,
    isSuccess: sendFormSuccess,
    isPending,
  } = useMutation({
    mutationFn: async (data) => {
      const response = await axios.post(EVENT_INQUIRY_API_URL, data);
      return response.data;
    },

    onSuccess: (data) => {
      setSubmittedData((prev) => ({ ...prev, ins_date: data.data.ins_date }));
    },
  });

  const isLoading = isPending || isEmailPending;

  const watchData = watch();

  useEffect(() => {
    if (sendFormSuccess && !sendEmailSuccess) {
      const customerEmailData = {
        template_id: eventEmailIdPerLanguage(),
        target: [{ email: submittedData?.email }],
        content: {
          first_name: submittedData?.first_name,
          last_name: submittedData?.last_name,
          phone: submittedData?.phone,
          company_name: submittedData?.company_name,
          segment: getCodeName(segmentCategory, submittedData?.segment),
          business: intl.formatMessage({ id: 'lang-event' }),
          rooms: submittedData?.rooms,
          event_type: getCodeName(meetingEventCategory, submittedData?.event_type),
          event_name: submittedData?.event_name,
          attendees: submittedData?.attendees,
          arrival_date:
            submittedData?.arrival_date === 'Invalid Date' ? '' : submittedData?.arrival_date,
          departure_date:
            submittedData?.departure_date === 'Invalid Date' ? '' : submittedData?.departure_date,
          email: submittedData?.email,
          comments: submittedData?.comments,
        },
      };

      // BEFORE-BUILD: 주석 지우기
      const managerEmailData = {
        template_id: process.env.REACT_APP_EVENT_EMAIL_INSPIRE_ID,
        target: [
          { email: process.env.REACT_APP_EVENT_EMAIL_MANAGE_1 },
          { email: process.env.REACT_APP_EVENT_EMAIL_MANAGE_2 },
          { email: process.env.REACT_APP_EVENT_EMAIL_MANAGE_3 },
          { email: process.env.REACT_APP_EVENT_EMAIL_MANAGE_4 },
          // { email: process.env.REACT_APP_EVENT_EMAIL_MANAGE_10 },
        ],
        content: {
          ins_date: submittedData?.ins_date,
          first_name: submittedData?.first_name,
          last_name: submittedData?.last_name,
          phone: submittedData?.phone,
          company_name: submittedData?.company_name,
          segment: getCodeName(segmentCategory, submittedData?.segment),
          business: 'Groups & Events',
          rooms: submittedData?.rooms,
          event_type: getCodeName(meetingEventCategory, submittedData?.event_type),
          event_name: submittedData?.event_name,
          attendees: submittedData?.attendees,
          arrival_date:
            submittedData?.arrival_date !== 'Invalid Date' ? submittedData?.arrival_date : '',
          departure_date:
            submittedData?.departure_date !== 'Invalid Date' ? submittedData?.departure_date : '',
          email: submittedData?.email,
          comments: submittedData?.comments,
        },
      };

      postEmailForm(customerEmailData);
      postEmailForm(managerEmailData);
    }

    if (sendEmailSuccess) {
      navigate(`/${lang}/complete`, { state: { complete: true, type: 'arena' } });
    }
  }, [
    sendEmailSuccess,
    lang,
    navigate,
    sendFormSuccess,
    submittedData,
    meetingEventCategory,
    postEmailForm,
    intl,
    segmentCategory,
  ]);

  useEffect(() => {
    // NOTE: phone 쓰고 다시 지웠을 때 undefined로 떠서 validation 문구 required로 뜨는 문제
    if (watchData.phone === undefined) {
      setValue('phone', '');
    }
  }, [watchData.phone, setValue]);

  const submitHandler = useCallback(
    async (data) => {
      const { termsAll, termsReq1, termsReq2, ...restData } = data;

      for (const key in restData) {
        if (restData[key] === '') restData[key] = null;
      }

      // attendees, rooms, budget 은 number 형태로 보내야 함
      const changeToNumber = {
        ...restData,
        event_type: eventType,
        attendees: Number(removeCommaFromNumber(restData.attendees)),
        rooms: Number(removeCommaFromNumber(restData.rooms)),
        arrival_date: changeDateFormat(restData.arrival_date),
        departure_date: changeDateFormat(restData.departure_date),
        language: localStorage.getItem(LOCAL_LANGUAGE),
      };

      setSubmittedData(changeToNumber);
      eventMutate(changeToNumber);
      // console.log('--- Event API 전송 예정 데이터 ---', changeToNumber);
    },
    [eventMutate, eventType]
  );

  return (
    <InputForm onSubmit={handleSubmit(submitHandler)}>
      {step === 1 && (
        <EventClientInformation
          trigger={trigger}
          control={control}
          setStep={setStep}
          watch={watch}
          errors={errors}
          setValue={setValue}
        />
      )}

      {step === 2 && (
        <EventRequestDetails
          control={control}
          setStep={setStep}
          setValue={setValue}
          errors={errors}
          isPending={isLoading}
        />
      )}
    </InputForm>
  );
};

export default EventForm;
